import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Answers, GetTotalInput, PolicyDetail, Question, QuestionType, Questions, SaveItem, SavedSurveyInfo } from 'models';
import { Subject } from 'rxjs';
import { ApiService } from 'src/_utility/api';
import { SurveyUtility } from 'src/_utility/survey';
import { StorageService } from './storage.service';
import { LoginService } from './login.service';
import { SessionStorageKeys } from 'utility';
import { BrowserStorageService } from './browser-storage.service';
import { DOCUMENT } from '@angular/common';
import { BrowserConfig } from './config';

@Injectable({
  providedIn: 'root'
})
export class SurveyService {

  private questions: Questions = undefined;
  private SurveyResults: PolicyDetail[] = undefined
  
  private savedSurvey: SaveItem = undefined;
  private savedSurveyInfo: SavedSurveyInfo = undefined;
  private savedSurveys: SaveItem[] = undefined;

  answerUpdated: Subject<string> = new Subject();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private api: ApiService,
    private snackBar: MatSnackBar,
    private storage: StorageService,
    private browserStorage: BrowserStorageService
  ) { }

  //#region Questions

  restart() {
    this.resetQuestions();
    this.savedSurvey = undefined;
  }

  resetQuestions() {
    if(this.questions != null) {
      this.questions.forEach(q => {
        q.isHidden = true;
        q.answers.forEach(a => {
          a.isSelected = false;
          a.value1 = null;
          a.value2 = null;
        });
      });
      if(this.questions[0] != null) {
        this.questions[0].isHidden = false;
      }
    }
  }

  loadQuestions(callback: () => void = null) {
    this.questions = this.storage.getQuestions();
    if (this.questions) {
      if (callback != null) {callback();}
    }
    else {
      this.api.getQuestions()
        .then(data => {
          if (data != null) {
            const questionsMapped = Question.mapData(data.Questions);
            this.questions = questionsMapped;
            this.storage.setQuestions(this.questions);
            if (callback != null) {callback();}
          }
          else {
            this.questions = [];
            this.storage.setQuestions(this.questions);
            if (callback != null) {callback();}
          }
        })
        .catch(err => {
          this.snackBar.open("E' stato riscontrato un errore durante il caricamento delle domande", 'Chiudi', { duration: 5000});
          this.questions = [];
          this.storage.setQuestions(this.questions);
          if (callback != null) {callback();}
        })
    }
  }
  
  getQuestions(checkTmp: boolean = false): Questions {
    if (checkTmp) {
      const tmpSurveySave = this.browserStorage.getData(SessionStorageKeys.surveyTmpSaveKey, 'sessionStorage');
      if (tmpSurveySave) {
        this.resetQuestions();
        this.questions = JSON.parse(tmpSurveySave) as Questions;
        this.browserStorage.removeData(SessionStorageKeys.surveyTmpSaveKey, 'sessionStorage');
      }
    }
    if (this.questions) {
      this.questions.forEach((q, i) => {
        if (q.id != '1.0') {
          q.prevQuestion = i > 0 ? SurveyUtility.getPrevQuestion(q, this.questions) : null;
          q.nextQuestion = SurveyUtility.getNextQuestion(q, this.questions);

          const saveOfQ = this.savedSurvey?.Search?.Questions.find(x => x.Code == q.id);
          if (saveOfQ) {
            const savedAnswer = saveOfQ.Answers[0];
            const answer = q.answers.find(a => a.id == savedAnswer.Code);
            answer.isSelected = true;
            answer.value1 = savedAnswer.Value1;
            answer.value2 = savedAnswer.Value2;
            
            // Lato backend viene gestito in mesi, 
            // ma nella domanda viene chiesto di inserire il valore in anni,
            // pertanto, è necessario dividere il valore tornato in 12
            if (q.id == BrowserConfig.redemptionQuestion) {
              answer.value1 = (parseInt(answer.value1) / 12).toString();
            }
          }

          q.isHidden = i > 0 ? SurveyUtility.isQuestionHidden(q, this.questions) : false;
        }
      })

      this.savedSurvey = undefined;
    }

    return this.questions
  }

  getQuestionById(fQuestionId: string): Question {
    return this.questions?.find(q => q.id == fQuestionId);
  }

  scrollToQuestion(fQuestion: Question) {
    if (fQuestion && (fQuestion.id != '1.10.10' || SurveyUtility.isQuestionAnswered(fQuestion))) {
      let qContainer = this.document.getElementById(`Question_Container_${fQuestion?.id}`);
  
      if(qContainer != null) {
        setTimeout(() => {
          fQuestion.isScrollTo = false;
          qContainer.scrollIntoView({block: 'center'});
        }, 300);
      }

    }
    else if (fQuestion) {
      fQuestion.isScrollTo = false;
    }
  }

  //#endregion Questions

  //#region Survey results

  loadSurveyResults(input: GetTotalInput, callback: () => void = null) {
    this.api.postSurveyResults(input)
      .then(data => {
        this.SurveyResults = data?.Instruments;
        if (callback != null) {callback();}
      })
      .catch(err => {
        this.snackBar.open("E' stato riscontrato un errore durante il caricamento delle polizze", 'Chiudi', { duration: 5000 });
        this.SurveyResults = [];
        if (callback != null) {callback();}
      })
  }

  getSurveyResults(): PolicyDetail[] {
    return this.SurveyResults ?? []
  }

  //#endregion Survey results
  
  //#region Save loadings

  setSavedSurveyInfo(fInfo: SavedSurveyInfo) {
    this.savedSurveyInfo = fInfo;
  }

  getSavedSurveyInfo() {
    return this.savedSurveyInfo
  }

  loadSavedSurveys(callback: () => void = undefined) {
    this.api.getSavedSurveys()
      .then(data => {
        this.savedSurveys = data;
        callback();
      })
      .catch(err => {
        this.savedSurveys = [];
        callback();
      })
  }

  getSavedSurveys(): SaveItem[] {
    return this.savedSurveys ?? []
  }

  loadSavedSurveyById(surveyId: string, callback: () => void = null) {
    this.api.getSavedSurveyById(surveyId)
      .then(data => {
        if (!data) {
          this.snackBar.open("La ricerca non è stata trovata", 'Chiudi', { duration: 5000 });
        }

        this.updateSavedSurvey(data);
        if (callback != null) {callback();}
      })
      .catch(err => {
        this.snackBar.open("E' stato riscontrato un errore durante il caricamento della ricerca salvata", 'Chiudi', { duration: 5000 });
        this.updateSavedSurvey(null);
        if (callback != null) {callback();}
      })
  }

  saveSurvey(fSurvey: SaveItem, successCallback: () => void = null, errorCallback: () => void = null) {
    if (fSurvey) {
      if (fSurvey.Id == null || fSurvey.Id == undefined || isNaN(fSurvey.Id)) { fSurvey.Id = 0; }
      this.api.saveSurvey(fSurvey)
        .then(data => {
          if (successCallback != null) { successCallback(); }
        })
        .catch(err => {
          if (errorCallback != null) { errorCallback(); }
        }) 
    }
  }

  updateSavedSurvey(fSurvey: SaveItem) {
    this.savedSurvey = fSurvey;
    if (fSurvey) { this.setSavedSurveyInfo({id: fSurvey.Id.toString(), name: fSurvey.Name}); }
  }

  deleteSavedSurveyById(fSurveyId: string, callback: (() => void) = null) {
    if (fSurveyId) {
      this.api.deleteSavedSurveyById(fSurveyId)
        .then(data => {
          if (callback) { callback(); }
        })
        .catch(err => {
          if (callback) { callback(); }
        })
    }
  }

  getSavedSurvey(): SaveItem {
    return this.savedSurvey
  }

  //#endregion Save loadings

}
